import { Icon, Icons } from '@/components/icon';
import { ContactAvatar } from '@/components/inbox/contact-avatar';
import { Checkbox } from '@/components/ui/checkbox';
import { Popover, PopoverContent } from '@/components/ui/popover';
import { ScrollArea } from '@/components/ui/scroll-area';
import { P3 } from '@/components/ui/text';
import { useTranslation } from '@/hooks/use-translation';
import { cn } from '@/lib/utils';
import type { components } from '@/services/api-allo/api-allo-v1';
import { $apiAllo } from '@/services/api-allo/client';
import { PopoverAnchor } from '@radix-ui/react-popover';
import { Plus } from 'lucide-react';
import * as React from 'react';
import { TableCell } from '../table-cell';

type CompanyMember = components['schemas']['CompanyMemberResponse'];
type Contact = components['schemas']['Contact'];

export interface TeamCellProps {
  members?: CompanyMember[] | null;
  companyId?: string;
  isOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  onMembersChange?: (
    companyId: string,
    memberIds: string[],
    addedContact?: { id: string; name: string; last_name: string },
  ) => void;
  onCreateContact?: (companyId: string) => void;
  onMemberClick?: (memberId: string) => void;
}

export function getMemberName(member: CompanyMember): string {
  const firstName = member.name ?? '';
  const lastName = member.last_name ?? '';
  return `${firstName} ${lastName}`.trim();
}

function getContactName(contact: Contact): string {
  const firstName = contact.name ?? '';
  const lastName = contact.last_name ?? '';
  return `${firstName} ${lastName}`.trim();
}

function TeamMember({
  member,
  onMemberClick,
}: { member: CompanyMember; onMemberClick?: (memberId: string) => void }) {
  const name = getMemberName(member);

  const content = (
    <>
      <div className="flex max-h-5 items-center gap-1">
        <ContactAvatar name={name} size="2xs" imageUrl={member.image_url ?? undefined} />
        <P3 className="whitespace-nowrap text-primary-100">{name}</P3>
      </div>
      <span className="h-px w-full bg-primary-10 group-hover/member:opacity-0" />
    </>
  );

  if (onMemberClick && member.id) {
    return (
      <button
        type="button"
        className="group/member flex flex-none flex-col px-1 pt-0.5 hover:bg-primary-5 rounded-xs cursor-pointer"
        onClick={(e) => {
          e.stopPropagation();
          onMemberClick(member.id!);
        }}
      >
        {content}
      </button>
    );
  }

  return (
    <div className="group/member flex flex-none flex-col px-1 pt-0.5 hover:bg-primary-5 rounded-xs">
      {content}
    </div>
  );
}

interface ContactRowProps {
  name: string;
  isSelected: boolean;
  onClick: () => void;
}

function ContactRow({ name, isSelected, onClick }: ContactRowProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'w-full h-7 flex items-center justify-between px-2 rounded-md transition-colors cursor-pointer hover:bg-primary-3',
      )}
    >
      <div className="flex items-center gap-1 min-w-0 flex-1">
        <ContactAvatar name={name} size="2xs" className="shrink-0" />
        <P3 className="text-primary-100 truncate">{name}</P3>
      </div>
      <Checkbox checked={isSelected} className="size-3 pointer-events-none" />
    </button>
  );
}

function useTeamContactsSearch(searchValue: string, isOpen: boolean) {
  // Use the same POST filter endpoint as the contacts table
  const { data, isLoading } = $apiAllo.useQuery(
    'post',
    '/v2/contact/filter',
    {
      body: {
        filter: {
          and: searchValue.trim()
            ? [{ field: 'name', operator: 'contains', value: searchValue.trim() }]
            : [],
        },
        sort: [{ field: 'last_activity_date', order: 'desc' }],
        pagination: { page: 0, size: 10 },
      },
    },
    {
      enabled: isOpen,
      staleTime: 5000,
    },
  );

  const contacts = React.useMemo(() => {
    return (data?.data as Contact[]) ?? [];
  }, [data?.data]);

  return {
    contacts,
    isLoading,
  };
}

export interface TeamCellHandle {
  open: () => void;
}

export const TeamCell = React.memo(
  React.forwardRef<TeamCellHandle, TeamCellProps>(function TeamCell(
    {
      members,
      companyId,
      isOpen: controlledIsOpen,
      onOpenChange,
      onMembersChange,
      onCreateContact,
      onMemberClick,
    },
    ref,
  ) {
    const [internalIsOpen, setInternalIsOpen] = React.useState(false);
    const triggerRef = React.useRef<HTMLDivElement>(null);

    // Use controlled state if provided, otherwise use internal state
    const isControlled = controlledIsOpen !== undefined;
    const isOpen = isControlled ? controlledIsOpen : internalIsOpen;
    const setIsOpen = React.useCallback(
      (open: boolean) => {
        if (isControlled) {
          onOpenChange?.(open);
        } else {
          setInternalIsOpen(open);
        }
      },
      [isControlled, onOpenChange],
    );

    // Get member IDs for checking selection
    const memberIds = React.useMemo(
      () => (members ?? []).map((m) => m.id).filter((id): id is string => !!id),
      [members],
    );

    const multiple = members?.length ? members.length > 1 : false;

    React.useImperativeHandle(
      ref,
      () => ({
        open: () => {
          setIsOpen(true);
        },
      }),
      [setIsOpen],
    );

    const handleMembersChange = React.useCallback(
      (
        companyId: string,
        memberIds: string[],
        addedContact?: { id: string; name: string; last_name: string },
      ) => {
        onMembersChange?.(companyId, memberIds, addedContact);
      },
      [onMembersChange],
    );

    const handleOpenChange = React.useCallback(
      (newOpen: boolean) => {
        // Only propagate if the state is actually changing
        if (newOpen === isOpen) {
          return;
        }
        setIsOpen(newOpen);
        // When closing the popover, restore focus to the original cell
        if (!newOpen) {
          const cell = triggerRef.current?.closest('[tabindex]') as HTMLElement | null;
          if (cell) {
            requestAnimationFrame(() => {
              cell.focus();
            });
          }
        }
      },
      [setIsOpen, isOpen],
    );

    const cellContent = (
      <TableCell
        className={cn(
          'overflow-hidden px-2',
          multiple && 'gap-1',
          multiple &&
            'group-focus:min-h-[65px] group-focus:max-h-[192px] group-focus:overflow-y-auto group-focus:flex-col group-focus:items-start group-focus:py-2.5 group-focus-within:min-h-[65px] group-focus-within:max-h-[192px] group-focus-within:overflow-y-auto group-focus-within:flex-col group-focus-within:items-start group-focus-within:py-2.5',
        )}
      >
        {members?.map((member, index) => {
          if (!member) {
            return null;
          }
          return (
            <TeamMember key={member.id ?? index} member={member} onMemberClick={onMemberClick} />
          );
        })}
      </TableCell>
    );

    // If no companyId, just render the cell without popover (read-only mode)
    if (!companyId) {
      return cellContent;
    }

    return (
      <TeamPopover
        members={members ?? []}
        memberIds={memberIds}
        companyId={companyId}
        open={isOpen}
        onOpenChange={handleOpenChange}
        onMembersChange={handleMembersChange}
        onCreateContact={onCreateContact}
        triggerRef={triggerRef}
      >
        {cellContent}
      </TeamPopover>
    );
  }),
);

export interface TeamPopoverProps {
  children: React.ReactNode;
  members: CompanyMember[];
  memberIds: string[];
  companyId: string;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  onMembersChange?: (
    companyId: string,
    memberIds: string[],
    addedContact?: { id: string; name: string; last_name: string },
  ) => void;
  onCreateContact?: (companyId: string) => void;
  triggerRef?: React.RefObject<HTMLDivElement | null>;
  align?: 'start' | 'center' | 'end';
  sideOffset?: number;
  contentClassName?: string;
}

export function TeamPopover({
  children,
  members,
  memberIds,
  companyId,
  open: controlledOpen,
  onOpenChange,
  onMembersChange,
  onCreateContact,
  triggerRef: externalTriggerRef,
  align = 'start',
  sideOffset = -41,
  contentClassName,
}: TeamPopoverProps) {
  const { t } = useTranslation('desktop_ui');
  const internalTriggerRef = React.useRef<HTMLDivElement>(null);
  const triggerRef = externalTriggerRef ?? internalTriggerRef;
  const inputRef = React.useRef<HTMLInputElement>(null);
  const [internalOpen, setInternalOpen] = React.useState(false);
  const [searchValue, setSearchValue] = React.useState('');

  // Support both controlled and uncontrolled modes
  const isControlled = controlledOpen !== undefined;
  const open = isControlled ? controlledOpen : internalOpen;
  const setOpen = React.useCallback(
    (value: boolean) => {
      if (isControlled) {
        onOpenChange?.(value);
      } else {
        setInternalOpen(value);
      }
    },
    [isControlled, onOpenChange],
  );

  const { contacts, isLoading } = useTeamContactsSearch(searchValue, open);

  // Filter out already selected members from search results
  const filteredContacts = React.useMemo(
    () => contacts.filter((contact) => !memberIds.includes(contact.id ?? '')),
    [contacts, memberIds],
  );

  // Filter selected members by search value
  const filteredMembers = React.useMemo(() => {
    if (!searchValue.trim()) {
      return members;
    }
    const searchLower = searchValue.toLowerCase().trim();
    return members.filter((member) => {
      const name = getMemberName(member).toLowerCase();
      return name.includes(searchLower);
    });
  }, [members, searchValue]);

  const hasFilteredMembers = filteredMembers.length > 0;

  // Reset search when popover closes
  React.useEffect(() => {
    if (!open) {
      setSearchValue('');
    }
  }, [open]);

  const handleToggleMember = React.useCallback(
    (contactId: string, contact?: Contact) => {
      if (!onMembersChange || !companyId) {
        return;
      }

      const isSelected = memberIds.includes(contactId);
      if (isSelected) {
        onMembersChange(
          companyId,
          memberIds.filter((id) => id !== contactId),
        );
      } else {
        // Pass contact info for optimistic updates
        const addedContact = contact
          ? { id: contactId, name: contact.name ?? '', last_name: contact.last_name ?? '' }
          : undefined;
        onMembersChange(companyId, [...memberIds, contactId], addedContact);
      }
    },
    [memberIds, onMembersChange, companyId],
  );

  const handleOpenChange = React.useCallback(
    (newOpen: boolean) => {
      // Only propagate if the state is actually changing
      if (newOpen === open) {
        return;
      }
      setOpen(newOpen);
      // When closing the popover, restore focus to the original cell
      if (!newOpen) {
        const cell = triggerRef.current?.closest('[tabindex]') as HTMLElement | null;
        if (cell) {
          requestAnimationFrame(() => {
            cell.focus();
          });
        }
      }
    },
    [setOpen, open, triggerRef],
  );

  const handleCreateContact = React.useCallback(() => {
    setOpen(false);
    onCreateContact?.(companyId);
  }, [setOpen, onCreateContact, companyId]);

  return (
    <Popover open={open} onOpenChange={handleOpenChange} modal>
      <PopoverAnchor
        onDoubleClick={() => {
          setOpen(true);
        }}
        ref={triggerRef}
      >
        {children}
      </PopoverAnchor>
      <PopoverContent
        align={align}
        sideOffset={sideOffset}
        className={cn(
          'z-popover w-[var(--radix-popover-trigger-width)] min-w-[240px] rounded-2xl bg-white p-0',
          contentClassName,
        )}
        onOpenAutoFocus={(e) => {
          e.preventDefault();
          inputRef.current?.focus();
        }}
      >
        {/* Search input */}
        <div className="flex h-7 items-center gap-1 px-2 py-1">
          <Icon name={Icons.MagnifyingGlass} className="text-s text-primary-50 leading-0" />
          <input
            ref={inputRef}
            type="text"
            placeholder={t('components.companies.teamMember.searchContacts')}
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            className="flex-1 bg-transparent font-inter font-medium text-primary-100 text-s caret-highlight-default outline-none placeholder:text-primary-60"
          />
        </div>

        <div className="h-px w-full bg-primary-10" />

        {/* Contacts list */}
        <ScrollArea className="max-h-[160px]" variant="popover">
          <div className="flex flex-col gap-0.5 p-1">
            {/* Selected members section (filtered by search) */}
            {hasFilteredMembers &&
              filteredMembers.map((member) => {
                if (!member?.id) {
                  return null;
                }
                return (
                  <ContactRow
                    key={member.id}
                    name={getMemberName(member)}
                    isSelected
                    onClick={() => handleToggleMember(member.id!)}
                  />
                );
              })}

            {/* Search results / All contacts section */}
            {isLoading ? (
              <div className="py-3 text-center">
                <P3 className="text-primary-40">{t('components.contacts.table.loading')}</P3>
              </div>
            ) : filteredContacts.length > 0 ? (
              <>
                {hasFilteredMembers && <div className="h-px w-full bg-primary-10 my-0.5" />}
                {filteredContacts.map((contact) => (
                  <ContactRow
                    key={contact.id}
                    name={getContactName(contact)}
                    isSelected={false}
                    onClick={() => contact.id && handleToggleMember(contact.id, contact)}
                  />
                ))}
              </>
            ) : !hasFilteredMembers ? (
              <div className="py-3 text-center">
                <P3 className="text-primary-40">{t('components.sidebar.noResults')}</P3>
              </div>
            ) : null}
          </div>
        </ScrollArea>

        {/* Create Contact button */}
        {onCreateContact && (
          <>
            <div className="h-px w-full bg-primary-10" />
            <button
              type="button"
              onClick={handleCreateContact}
              className="w-full flex items-center gap-1 px-2 py-1.5 transition-colors hover:bg-primary-3 cursor-pointer"
            >
              <Plus className="size-[10.667px] text-primary-80" />
              <P3 className="text-primary-90 font-medium">
                {t('components.companies.teamMember.createContact')}
              </P3>
            </button>
          </>
        )}
      </PopoverContent>
    </Popover>
  );
}
